const DEFAULT_AUDIO_WAVEFORM_BAR_COUNT = 72;
const DEFAULT_AUDIO_WAVEFORM_BAR_WIDTH_PX = 4;
const DEFAULT_AUDIO_WAVEFORM_BAR_GAP_PX = 3;
const DEFAULT_AUDIO_WAVEFORM_VISIBLE_WINDOW_MS = 4200;
const DEFAULT_AUDIO_WAVEFORM_MIN_SAMPLE_INTERVAL_MS = 24;
const DEFAULT_AUDIO_WAVEFORM_MIN_BAR_HEIGHT = 0.14;
const DEFAULT_AUDIO_WAVEFORM_MAX_BAR_HEIGHT = 1;
const DEFAULT_AUDIO_WAVEFORM_SMOOTHING = 0.35;
const AUDIO_WAVEFORM_TIME_DOMAIN_CENTER = 128;
const AUDIO_WAVEFORM_NOISE_FLOOR = 0.05;
const AUDIO_WAVEFORM_SAMPLE_GAIN = 1.25;
const AUDIO_WAVEFORM_RESPONSE_EXPONENT = 1.5;
const AUDIO_WAVEFORM_NORMALIZED_REFERENCE_PEAK = 0.32;
const UNSIGNED_32_BIT_MODULUS = 4294967296;
const MIN_AUDIO_WAVEFORM_DYNAMIC_RANGE_SPAN = 0.0001;

/**
 * AudioNote's live recorder needs both a MediaRecorder chunk cadence and a
 * stable visible time window for the scrolling waveform.
 */
interface AudioNoteRecordingWaveformConfig {
    chunkTimesliceMs: number,
    visibleWindowMs: number,
}

interface AudioWaveformBuildOptions {
    normalizedReferencePeak: number,
    sampleLevelGain: number,
    useNormalizedReferencePeakFloor?: boolean,
    dynamicRangeFloorPercentile: number,
    dynamicRangeCeilingPercentile: number,
    dynamicRangeMinimumSpanRatio: number,
    amplitudeNoiseFloor: number,
    amplitudeSampleGain: number,
    amplitudeResponseExponent: number,
}

export const AUDIO_NOTE_RECORDING_WAVEFORM_CONFIG: AudioNoteRecordingWaveformConfig = Object.freeze({
    chunkTimesliceMs: 120,
    visibleWindowMs: DEFAULT_AUDIO_WAVEFORM_VISIBLE_WINDOW_MS,
});

/**
 * Live recording needs a little extra lift so normal speech is visible before
 * the note is saved and decoded.
 */
export const LIVE_AUDIO_NOTE_WAVEFORM_BUILD_OPTIONS: AudioWaveformBuildOptions = Object.freeze({
    normalizedReferencePeak: 0.14,
    sampleLevelGain: 2.5,
    dynamicRangeFloorPercentile: 0.12,
    dynamicRangeCeilingPercentile: 0.98,
    dynamicRangeMinimumSpanRatio: 0.12,
    amplitudeNoiseFloor: 0.03,
    amplitudeSampleGain: 1.45,
    amplitudeResponseExponent: 1.18,
});

/**
 * Completed notes should keep the same overall character as live recording,
 * but with slightly calmer scaling and note-specific dynamic-range mapping.
 */
export const COMPLETED_AUDIO_NOTE_WAVEFORM_BUILD_OPTIONS: AudioWaveformBuildOptions = Object.freeze({
    normalizedReferencePeak: 0.14,
    sampleLevelGain: 1.5,
    useNormalizedReferencePeakFloor: false,
    dynamicRangeFloorPercentile: 0.12,
    dynamicRangeCeilingPercentile: 0.98,
    dynamicRangeMinimumSpanRatio: 0.12,
    amplitudeNoiseFloor: 0.03,
    amplitudeSampleGain: 1.45,
    amplitudeResponseExponent: 1.18,
});

const resolveWaveformBarCount = (barCount: number): number => {
    if (!Number.isFinite(barCount) || barCount <= 0) {
        return DEFAULT_AUDIO_WAVEFORM_BAR_COUNT;
    }

    return Math.max(1, Math.floor(barCount));
};

/**
 * Derive how many bars fit in the inline waveform lane while keeping each bar
 * and gap at a consistent visual size across notes of different lengths.
 */
export const resolveAudioWaveformBarCountFromWidth = ({
    width,
    fallbackBarCount = DEFAULT_AUDIO_WAVEFORM_BAR_COUNT,
    barWidthPx = DEFAULT_AUDIO_WAVEFORM_BAR_WIDTH_PX,
    barGapPx = DEFAULT_AUDIO_WAVEFORM_BAR_GAP_PX,
}: {
    width: number,
    fallbackBarCount?: number,
    barWidthPx?: number,
    barGapPx?: number,
}): number => {
    if (!Number.isFinite(width) || width <= 0) {
        return resolveWaveformBarCount(fallbackBarCount);
    }

    const resolvedBarWidth = Math.max(1, Math.floor(barWidthPx));
    const resolvedBarGap = Math.max(0, Math.floor(barGapPx));
    const pixelsPerBar = resolvedBarWidth + resolvedBarGap;

    return Math.max(1, Math.floor((width + resolvedBarGap) / pixelsPerBar));
};

/**
 * Convert the visible bar count into a sampling cadence so the recording view
 * represents a stable time window across narrow and wide waveform lanes.
 */
export const resolveAudioWaveformSampleIntervalMs = ({
    visibleBarCount,
    visibleWindowMs = DEFAULT_AUDIO_WAVEFORM_VISIBLE_WINDOW_MS,
    minSampleIntervalMs = DEFAULT_AUDIO_WAVEFORM_MIN_SAMPLE_INTERVAL_MS,
}: {
    visibleBarCount: number,
    visibleWindowMs?: number,
    minSampleIntervalMs?: number,
}): number => {
    const resolvedVisibleBarCount = resolveWaveformBarCount(visibleBarCount);
    const resolvedVisibleWindowMs = Number.isFinite(visibleWindowMs)
        ? Math.max(1, Math.round(visibleWindowMs))
        : DEFAULT_AUDIO_WAVEFORM_VISIBLE_WINDOW_MS;
    const resolvedMinSampleIntervalMs = Number.isFinite(minSampleIntervalMs)
        ? Math.max(1, Math.round(minSampleIntervalMs))
        : DEFAULT_AUDIO_WAVEFORM_MIN_SAMPLE_INTERVAL_MS;

    return Math.max(
        resolvedMinSampleIntervalMs,
        Math.round(resolvedVisibleWindowMs / resolvedVisibleBarCount),
    );
};

const clampWaveformBarHeight = (
    value: number,
    minBarHeight: number,
    maxBarHeight: number,
): number => {
    if (!Number.isFinite(value)) {
        return minBarHeight;
    }

    return Math.min(maxBarHeight, Math.max(minBarHeight, value));
};

const normalizeWaveformBarHeight = (
    amplitude: number,
    minBarHeight: number,
    maxBarHeight: number,
): number => {
    const clampedAmplitude = Math.min(1, Math.max(0, amplitude));

    return clampWaveformBarHeight(
        minBarHeight + ((maxBarHeight - minBarHeight) * clampedAmplitude),
        minBarHeight,
        maxBarHeight,
    );
};

const shapeAudioWaveformAmplitude = ({
    amplitude,
    noiseFloor = AUDIO_WAVEFORM_NOISE_FLOOR,
    sampleGain = AUDIO_WAVEFORM_SAMPLE_GAIN,
    responseExponent = AUDIO_WAVEFORM_RESPONSE_EXPONENT,
}: {
    amplitude: number,
    noiseFloor?: number,
    sampleGain?: number,
    responseExponent?: number,
}): number => {
    const resolvedNoiseFloor = Number.isFinite(noiseFloor)
        ? Math.min(0.95, Math.max(0, noiseFloor))
        : AUDIO_WAVEFORM_NOISE_FLOOR;
    const resolvedSampleGain = Number.isFinite(sampleGain)
        ? Math.max(0, sampleGain)
        : AUDIO_WAVEFORM_SAMPLE_GAIN;
    const resolvedResponseExponent = Number.isFinite(responseExponent)
        ? Math.max(0.01, responseExponent)
        : AUDIO_WAVEFORM_RESPONSE_EXPONENT;
    const boostedAmplitude = Math.min(
        1,
        Math.max(0, amplitude) * resolvedSampleGain,
    );
    if (boostedAmplitude <= resolvedNoiseFloor) {
        return 0;
    }

    const gatedAmplitude = (
        (boostedAmplitude - resolvedNoiseFloor)
        / (1 - resolvedNoiseFloor)
    );

    return gatedAmplitude ** resolvedResponseExponent;
};

const normalizeSampleLevel = ({
    sampleLevel,
    minSampleLevel = 0,
    maxSampleLevel,
    normalizedReferencePeak = AUDIO_WAVEFORM_NORMALIZED_REFERENCE_PEAK,
    sampleLevelGain = 1,
    useNormalizedReferencePeakFloor = true,
}: {
    sampleLevel: number,
    minSampleLevel?: number,
    maxSampleLevel: number,
    normalizedReferencePeak?: number,
    sampleLevelGain?: number,
    useNormalizedReferencePeakFloor?: boolean,
}): number => {
    if (!Number.isFinite(sampleLevel) || sampleLevel <= 0) {
        return 0;
    }

    if (!Number.isFinite(maxSampleLevel) || maxSampleLevel <= 0) {
        return 0;
    }

    const resolvedSampleLevelGain = Number.isFinite(sampleLevelGain)
        ? Math.max(0, sampleLevelGain)
        : 1;
    const gainedSampleLevel = sampleLevel * resolvedSampleLevelGain;
    const gainedMinSampleLevel = (
        Number.isFinite(minSampleLevel)
            ? Math.max(0, minSampleLevel)
            : 0
    ) * resolvedSampleLevelGain;
    const gainedMaxSampleLevel = maxSampleLevel * resolvedSampleLevelGain;
    const resolvedNormalizedReferencePeak = Number.isFinite(normalizedReferencePeak)
        ? Math.max(0.01, normalizedReferencePeak)
        : AUDIO_WAVEFORM_NORMALIZED_REFERENCE_PEAK;

    /**
     * Live microphone samples often arrive as already-normalized decimals that
     * still top out well below 1. Give that quiet range a stable reference peak
     * so spoken audio remains visible without making the noise floor jumpy.
     *
     * Completed notes can also pass a note-specific soft floor so the mapping
     * stretches the meaningful dynamic range instead of anchoring every note to
     * absolute zero.
     */
    const shouldUseNormalizedReferencePeakFloor = useNormalizedReferencePeakFloor !== false;
    let normalizationMax = gainedMaxSampleLevel;
    if (gainedMaxSampleLevel <= 1 && shouldUseNormalizedReferencePeakFloor) {
        normalizationMax = Math.max(
            gainedMaxSampleLevel,
            resolvedNormalizedReferencePeak,
        );
    }
    const normalizationFloor = Math.min(
        Math.max(0, gainedMinSampleLevel),
        Math.max(0, normalizationMax - MIN_AUDIO_WAVEFORM_DYNAMIC_RANGE_SPAN),
    );

    if (gainedSampleLevel <= normalizationFloor) {
        return 0;
    }

    return (
        (gainedSampleLevel - normalizationFloor)
        / Math.max(MIN_AUDIO_WAVEFORM_DYNAMIC_RANGE_SPAN, normalizationMax - normalizationFloor)
    );
};

const resolveWaveformQuantile = (
    sortedSampleLevels: number[],
    percentile: number,
): number => {
    if (sortedSampleLevels.length === 0) {
        return 0;
    }

    const clampedPercentile = Math.min(1, Math.max(0, percentile));
    const sourcePosition = clampedPercentile * (sortedSampleLevels.length - 1);
    const leftIndex = Math.floor(sourcePosition);
    const rightIndex = Math.min(sortedSampleLevels.length - 1, leftIndex + 1);
    const interpolationProgress = sourcePosition - leftIndex;

    return (
        (sortedSampleLevels[leftIndex] * (1 - interpolationProgress))
        + (sortedSampleLevels[rightIndex] * interpolationProgress)
    );
};

const resolveWaveformDynamicRangeBounds = ({
    sampleLevels,
    floorPercentile = 0,
    ceilingPercentile = 1,
    minimumSpanRatio = 0,
}: {
    sampleLevels: number[],
    floorPercentile?: number,
    ceilingPercentile?: number,
    minimumSpanRatio?: number,
}): {
    floorLevel: number,
    ceilingLevel: number,
} => {
    const positiveSampleLevels = sampleLevels
        .filter((sampleLevel) => Number.isFinite(sampleLevel) && sampleLevel > 0)
        .sort((left, right) => left - right);
    if (positiveSampleLevels.length === 0) {
        return {
            floorLevel: 0,
            ceilingLevel: 0,
        };
    }

    const resolvedFloorPercentile = Number.isFinite(floorPercentile)
        ? Math.min(0.95, Math.max(0, floorPercentile))
        : 0;
    const resolvedCeilingPercentile = Number.isFinite(ceilingPercentile)
        ? Math.min(1, Math.max(resolvedFloorPercentile + 0.01, ceilingPercentile))
        : 1;
    const resolvedMinimumSpanRatio = Number.isFinite(minimumSpanRatio)
        ? Math.max(0, minimumSpanRatio)
        : 0;
    const rawFloorLevel = resolveWaveformQuantile(
        positiveSampleLevels,
        resolvedFloorPercentile,
    );
    const rawCeilingLevel = resolveWaveformQuantile(
        positiveSampleLevels,
        resolvedCeilingPercentile,
    );
    const minimumSpan = Math.max(
        MIN_AUDIO_WAVEFORM_DYNAMIC_RANGE_SPAN,
        rawCeilingLevel * resolvedMinimumSpanRatio,
    );
    const ceilingLevel = Math.max(rawCeilingLevel, rawFloorLevel + minimumSpan);
    const floorLevel = Math.max(0, Math.min(rawFloorLevel, ceilingLevel - minimumSpan));

    return {
        floorLevel,
        ceilingLevel,
    };
};

const normalizeUnsigned32 = (value: number): number => {
    const normalizedValue = value % UNSIGNED_32_BIT_MODULUS;

    return normalizedValue < 0
        ? normalizedValue + UNSIGNED_32_BIT_MODULUS
        : normalizedValue;
};

/**
 * Returns the low resting bars used before live microphone input or decoded
 * playback data is available.
 */
export const buildSilentAudioWaveformBars = ({
    barCount = DEFAULT_AUDIO_WAVEFORM_BAR_COUNT,
    minBarHeight = DEFAULT_AUDIO_WAVEFORM_MIN_BAR_HEIGHT,
}: {
    barCount?: number,
    minBarHeight?: number,
} = {}): number[] => {
    const resolvedBarCount = resolveWaveformBarCount(barCount);

    return Array.from(
        { length: resolvedBarCount },
        () => clampWaveformBarHeight(
            minBarHeight,
            minBarHeight,
            DEFAULT_AUDIO_WAVEFORM_MAX_BAR_HEIGHT,
        ),
    );
};

/**
 * Samples decoded PCM data into the compact bar format used by the inline
 * audio-note waveform. RMS keeps louder regions visible without overreacting
 * to individual spikes.
 */
export const buildAudioWaveformBarsFromChannelData = ({
    channelData,
    barCount = DEFAULT_AUDIO_WAVEFORM_BAR_COUNT,
    minBarHeight = DEFAULT_AUDIO_WAVEFORM_MIN_BAR_HEIGHT,
    normalizedReferencePeak = AUDIO_WAVEFORM_NORMALIZED_REFERENCE_PEAK,
    sampleLevelGain = 1,
    useNormalizedReferencePeakFloor = true,
    dynamicRangeFloorPercentile = 0,
    dynamicRangeCeilingPercentile = 1,
    dynamicRangeMinimumSpanRatio = 0,
    amplitudeNoiseFloor = AUDIO_WAVEFORM_NOISE_FLOOR,
    amplitudeSampleGain = AUDIO_WAVEFORM_SAMPLE_GAIN,
    amplitudeResponseExponent = AUDIO_WAVEFORM_RESPONSE_EXPONENT,
}: {
    channelData: Float32Array,
    barCount?: number,
    minBarHeight?: number,
    normalizedReferencePeak?: number,
    sampleLevelGain?: number,
    useNormalizedReferencePeakFloor?: boolean,
    dynamicRangeFloorPercentile?: number,
    dynamicRangeCeilingPercentile?: number,
    dynamicRangeMinimumSpanRatio?: number,
    amplitudeNoiseFloor?: number,
    amplitudeSampleGain?: number,
    amplitudeResponseExponent?: number,
}): number[] => {
    const resolvedBarCount = resolveWaveformBarCount(barCount);
    if (!(channelData instanceof Float32Array) || channelData.length === 0) {
        return buildSilentAudioWaveformBars({
            barCount: resolvedBarCount,
            minBarHeight,
        });
    }

    const rmsAmplitudes: number[] = [];
    const samplesPerBar = Math.max(1, Math.ceil(channelData.length / resolvedBarCount));

    for (let barIndex = 0; barIndex < resolvedBarCount; barIndex += 1) {
        const startIndex = barIndex * samplesPerBar;
        const endIndex = Math.min(channelData.length, startIndex + samplesPerBar);
        let sumOfSquares = 0;
        let sampleCount = 0;

        for (let sampleIndex = startIndex; sampleIndex < endIndex; sampleIndex += 1) {
            const sample = channelData[sampleIndex] || 0;
            sumOfSquares += sample * sample;
            sampleCount += 1;
        }

        const rmsAmplitude = sampleCount > 0
            ? Math.sqrt(sumOfSquares / sampleCount)
            : 0;

        rmsAmplitudes.push(rmsAmplitude);
    }

    const maxRmsAmplitude = rmsAmplitudes.reduce((currentMax, rmsAmplitude) => (
        rmsAmplitude > currentMax ? rmsAmplitude : currentMax
    ), 0);
    if (maxRmsAmplitude <= 0) {
        return buildSilentAudioWaveformBars({
            barCount: resolvedBarCount,
            minBarHeight,
        });
    }
    const {
        floorLevel: minRmsAmplitude,
        ceilingLevel: normalizedMaxRmsAmplitude,
    } = resolveWaveformDynamicRangeBounds({
        sampleLevels: rmsAmplitudes,
        floorPercentile: dynamicRangeFloorPercentile,
        ceilingPercentile: dynamicRangeCeilingPercentile,
        minimumSpanRatio: dynamicRangeMinimumSpanRatio,
    });
    const effectiveMaxRmsAmplitude = normalizedMaxRmsAmplitude > 0
        ? normalizedMaxRmsAmplitude
        : maxRmsAmplitude;

    return rmsAmplitudes.map((rmsAmplitude) => normalizeWaveformBarHeight(
        shapeAudioWaveformAmplitude({
            amplitude: normalizeSampleLevel({
                sampleLevel: rmsAmplitude,
                minSampleLevel: minRmsAmplitude,
                maxSampleLevel: effectiveMaxRmsAmplitude,
                normalizedReferencePeak,
                sampleLevelGain,
                useNormalizedReferencePeakFloor,
            }),
            noiseFloor: amplitudeNoiseFloor,
            sampleGain: amplitudeSampleGain,
            responseExponent: amplitudeResponseExponent,
        }),
        minBarHeight,
        DEFAULT_AUDIO_WAVEFORM_MAX_BAR_HEIGHT,
    ));
};

/**
 * Samples live microphone analyser snapshots into the same bar scale as the
 * decoded waveform so the recording and completed states feel related.
 */
export const buildAudioWaveformBarsFromByteData = ({
    byteData,
    barCount = DEFAULT_AUDIO_WAVEFORM_BAR_COUNT,
    minBarHeight = DEFAULT_AUDIO_WAVEFORM_MIN_BAR_HEIGHT,
}: {
    byteData: Uint8Array,
    barCount?: number,
    minBarHeight?: number,
}): number[] => {
    const resolvedBarCount = resolveWaveformBarCount(barCount);
    if (!(byteData instanceof Uint8Array) || byteData.length === 0) {
        return buildSilentAudioWaveformBars({
            barCount: resolvedBarCount,
            minBarHeight,
        });
    }

    const nextBars: number[] = [];
    const samplesPerBar = Math.max(1, Math.ceil(byteData.length / resolvedBarCount));

    for (let barIndex = 0; barIndex < resolvedBarCount; barIndex += 1) {
        const startIndex = barIndex * samplesPerBar;
        const endIndex = Math.min(byteData.length, startIndex + samplesPerBar);
        let sumOfSquares = 0;
        let sampleCount = 0;

        for (let sampleIndex = startIndex; sampleIndex < endIndex; sampleIndex += 1) {
            const centeredSample = (byteData[sampleIndex] - AUDIO_WAVEFORM_TIME_DOMAIN_CENTER)
                / AUDIO_WAVEFORM_TIME_DOMAIN_CENTER;

            sumOfSquares += centeredSample * centeredSample;
            sampleCount += 1;
        }

        const rmsAmplitude = sampleCount > 0
            ? Math.sqrt(sumOfSquares / sampleCount)
            : 0;

        nextBars.push(normalizeWaveformBarHeight(
            shapeAudioWaveformAmplitude({
                amplitude: rmsAmplitude,
            }),
            minBarHeight,
            DEFAULT_AUDIO_WAVEFORM_MAX_BAR_HEIGHT,
        ));
    }

    return nextBars;
};

/**
 * Reduces a single analyser snapshot to one loudness sample so recording UIs
 * can append that moment in time to a scrolling waveform history.
 */
export const getAudioWaveformSampleLevelFromByteData = ({
    byteData,
}: {
    byteData: Uint8Array,
}): number => {
    if (!(byteData instanceof Uint8Array) || byteData.length === 0) {
        return 0;
    }

    let sumOfSquares = 0;
    let peakAmplitude = 0;

    for (let sampleIndex = 0; sampleIndex < byteData.length; sampleIndex += 1) {
        const centeredSample = (byteData[sampleIndex] - AUDIO_WAVEFORM_TIME_DOMAIN_CENTER)
            / AUDIO_WAVEFORM_TIME_DOMAIN_CENTER;
        const absoluteAmplitude = Math.abs(centeredSample);

        sumOfSquares += centeredSample * centeredSample;
        if (absoluteAmplitude > peakAmplitude) {
            peakAmplitude = absoluteAmplitude;
        }
    }

    const rmsAmplitude = Math.sqrt(sumOfSquares / byteData.length);
    const mixedAmplitude = Math.min(
        1,
        (rmsAmplitude * 0.55) + (peakAmplitude * 0.9),
    );

    return mixedAmplitude;
};

/**
 * Live microphone sampling should use float analyser snapshots so quieter
 * speech is not flattened by 8-bit quantization before we derive loudness.
 */
export const getAudioWaveformSampleLevelFromFloatTimeDomainData = ({
    timeDomainData,
}: {
    timeDomainData: Parameters<AnalyserNode['getFloatTimeDomainData']>[0],
}): number => {
    if (!(timeDomainData instanceof Float32Array) || timeDomainData.length === 0) {
        return 0;
    }

    let sumOfSquares = 0;
    let peakAmplitude = 0;

    for (let sampleIndex = 0; sampleIndex < timeDomainData.length; sampleIndex += 1) {
        const centeredSample = Math.max(-1, Math.min(1, timeDomainData[sampleIndex] || 0));
        const absoluteAmplitude = Math.abs(centeredSample);

        sumOfSquares += centeredSample * centeredSample;
        if (absoluteAmplitude > peakAmplitude) {
            peakAmplitude = absoluteAmplitude;
        }
    }

    const rmsAmplitude = Math.sqrt(sumOfSquares / timeDomainData.length);
    const mixedAmplitude = Math.min(
        1,
        (rmsAmplitude * 0.55) + (peakAmplitude * 0.9),
    );

    return mixedAmplitude;
};

/**
 * Converts a stream of normalized or raw positive level samples into the
 * compact waveform bar format used by the inline audio note UI.
 * Optional dynamic-range parameters let completed notes stretch the note's
 * soft floor and loud ceiling into a more expressive saved waveform.
 */
export const buildAudioWaveformBarsFromSampleLevels = ({
    sampleLevels,
    barCount = DEFAULT_AUDIO_WAVEFORM_BAR_COUNT,
    minBarHeight = DEFAULT_AUDIO_WAVEFORM_MIN_BAR_HEIGHT,
    normalizedReferencePeak = AUDIO_WAVEFORM_NORMALIZED_REFERENCE_PEAK,
    sampleLevelGain = 1,
    useNormalizedReferencePeakFloor = true,
    dynamicRangeFloorPercentile = 0,
    dynamicRangeCeilingPercentile = 1,
    dynamicRangeMinimumSpanRatio = 0,
    amplitudeNoiseFloor = AUDIO_WAVEFORM_NOISE_FLOOR,
    amplitudeSampleGain = AUDIO_WAVEFORM_SAMPLE_GAIN,
    amplitudeResponseExponent = AUDIO_WAVEFORM_RESPONSE_EXPONENT,
}: {
    sampleLevels: number[],
    barCount?: number,
    minBarHeight?: number,
    normalizedReferencePeak?: number,
    sampleLevelGain?: number,
    useNormalizedReferencePeakFloor?: boolean,
    dynamicRangeFloorPercentile?: number,
    dynamicRangeCeilingPercentile?: number,
    dynamicRangeMinimumSpanRatio?: number,
    amplitudeNoiseFloor?: number,
    amplitudeSampleGain?: number,
    amplitudeResponseExponent?: number,
}): number[] => {
    const resolvedBarCount = resolveWaveformBarCount(barCount);
    if (!Array.isArray(sampleLevels) || sampleLevels.length === 0) {
        return buildSilentAudioWaveformBars({
            barCount: resolvedBarCount,
            minBarHeight,
        });
    }

    const sanitizedSampleLevels = sampleLevels.map((sampleLevel) => (
        Number.isFinite(sampleLevel)
            ? Math.max(0, sampleLevel)
            : 0
    ));
    const maxSampleLevel = sanitizedSampleLevels.reduce((currentMax, sampleLevel) => (
        sampleLevel > currentMax ? sampleLevel : currentMax
    ), 0);
    if (maxSampleLevel <= 0) {
        return buildSilentAudioWaveformBars({
            barCount: resolvedBarCount,
            minBarHeight,
        });
    }
    const {
        floorLevel: minSampleLevel,
        ceilingLevel: normalizedMaxSampleLevel,
    } = resolveWaveformDynamicRangeBounds({
        sampleLevels: sanitizedSampleLevels,
        floorPercentile: dynamicRangeFloorPercentile,
        ceilingPercentile: dynamicRangeCeilingPercentile,
        minimumSpanRatio: dynamicRangeMinimumSpanRatio,
    });
    const effectiveMaxSampleLevel = normalizedMaxSampleLevel > 0
        ? normalizedMaxSampleLevel
        : maxSampleLevel;

    const samplesPerBar = Math.max(1, Math.ceil(sanitizedSampleLevels.length / resolvedBarCount));
    const nextBars: number[] = [];

    for (let barIndex = 0; barIndex < resolvedBarCount; barIndex += 1) {
        const startIndex = barIndex * samplesPerBar;
        const endIndex = Math.min(sanitizedSampleLevels.length, startIndex + samplesPerBar);
        let peakSampleLevel = 0;

        for (let sampleIndex = startIndex; sampleIndex < endIndex; sampleIndex += 1) {
            if (sanitizedSampleLevels[sampleIndex] > peakSampleLevel) {
                peakSampleLevel = sanitizedSampleLevels[sampleIndex];
            }
        }

        nextBars.push(normalizeWaveformBarHeight(
            shapeAudioWaveformAmplitude({
                amplitude: normalizeSampleLevel({
                    sampleLevel: peakSampleLevel,
                    minSampleLevel,
                    maxSampleLevel: effectiveMaxSampleLevel,
                    normalizedReferencePeak,
                    sampleLevelGain,
                    useNormalizedReferencePeakFloor,
                }),
                noiseFloor: amplitudeNoiseFloor,
                sampleGain: amplitudeSampleGain,
                responseExponent: amplitudeResponseExponent,
            }),
            minBarHeight,
            DEFAULT_AUDIO_WAVEFORM_MAX_BAR_HEIGHT,
        ));
    }

    return nextBars;
};

/**
 * Builds a fixed-width scrolling waveform where the newest sample sits on the
 * right edge and older samples shift left over time.
 */
export const buildScrollingAudioWaveformBarsFromSampleLevels = ({
    sampleLevels,
    barCount = DEFAULT_AUDIO_WAVEFORM_BAR_COUNT,
    minBarHeight = DEFAULT_AUDIO_WAVEFORM_MIN_BAR_HEIGHT,
    normalizedReferencePeak = AUDIO_WAVEFORM_NORMALIZED_REFERENCE_PEAK,
    sampleLevelGain = 1,
    useNormalizedReferencePeakFloor = true,
    dynamicRangeFloorPercentile = 0,
    dynamicRangeCeilingPercentile = 1,
    dynamicRangeMinimumSpanRatio = 0,
    amplitudeNoiseFloor = AUDIO_WAVEFORM_NOISE_FLOOR,
    amplitudeSampleGain = AUDIO_WAVEFORM_SAMPLE_GAIN,
    amplitudeResponseExponent = AUDIO_WAVEFORM_RESPONSE_EXPONENT,
}: {
    sampleLevels: number[],
    barCount?: number,
    minBarHeight?: number,
    normalizedReferencePeak?: number,
    sampleLevelGain?: number,
    useNormalizedReferencePeakFloor?: boolean,
    dynamicRangeFloorPercentile?: number,
    dynamicRangeCeilingPercentile?: number,
    dynamicRangeMinimumSpanRatio?: number,
    amplitudeNoiseFloor?: number,
    amplitudeSampleGain?: number,
    amplitudeResponseExponent?: number,
}): number[] => {
    const resolvedBarCount = resolveWaveformBarCount(barCount);
    if (!Array.isArray(sampleLevels) || sampleLevels.length === 0) {
        return buildSilentAudioWaveformBars({
            barCount: resolvedBarCount,
            minBarHeight,
        });
    }

    const trailingSampleLevels = sampleLevels.slice(-resolvedBarCount).map((sampleLevel) => (
        Number.isFinite(sampleLevel)
            ? Math.max(0, sampleLevel)
            : 0
    ));
    const maxSampleLevel = trailingSampleLevels.reduce((currentMax, sampleLevel) => (
        sampleLevel > currentMax ? sampleLevel : currentMax
    ), 0);
    if (maxSampleLevel <= 0) {
        return buildSilentAudioWaveformBars({
            barCount: resolvedBarCount,
            minBarHeight,
        });
    }
    const {
        floorLevel: minSampleLevel,
        ceilingLevel: normalizedMaxSampleLevel,
    } = resolveWaveformDynamicRangeBounds({
        sampleLevels: trailingSampleLevels,
        floorPercentile: dynamicRangeFloorPercentile,
        ceilingPercentile: dynamicRangeCeilingPercentile,
        minimumSpanRatio: dynamicRangeMinimumSpanRatio,
    });
    const effectiveMaxSampleLevel = normalizedMaxSampleLevel > 0
        ? normalizedMaxSampleLevel
        : maxSampleLevel;

    const normalizedBars = trailingSampleLevels.map((sampleLevel) => normalizeWaveformBarHeight(
        shapeAudioWaveformAmplitude({
            amplitude: normalizeSampleLevel({
                sampleLevel,
                minSampleLevel,
                maxSampleLevel: effectiveMaxSampleLevel,
                normalizedReferencePeak,
                sampleLevelGain,
                useNormalizedReferencePeakFloor,
            }),
            noiseFloor: amplitudeNoiseFloor,
            sampleGain: amplitudeSampleGain,
            responseExponent: amplitudeResponseExponent,
        }),
        minBarHeight,
        DEFAULT_AUDIO_WAVEFORM_MAX_BAR_HEIGHT,
    ));
    if (normalizedBars.length < resolvedBarCount) {
        return fitAudioWaveformBarsToBarCount({
            bars: normalizedBars,
            barCount: resolvedBarCount,
            minBarHeight,
        });
    }

    return normalizedBars;
};

/**
 * Live recording bars should keep the height they had when they were first
 * captured. This helper only scrolls or stretches already-shaped bars instead
 * of renormalizing the older bars on every tick.
 */
export const buildScrollingAudioWaveformBarsFromCapturedBars = ({
    bars,
    barCount = DEFAULT_AUDIO_WAVEFORM_BAR_COUNT,
    minBarHeight = DEFAULT_AUDIO_WAVEFORM_MIN_BAR_HEIGHT,
}: {
    bars: number[],
    barCount?: number,
    minBarHeight?: number,
}): number[] => {
    const resolvedBarCount = resolveWaveformBarCount(barCount);
    if (!Array.isArray(bars) || bars.length === 0) {
        return buildSilentAudioWaveformBars({
            barCount: resolvedBarCount,
            minBarHeight,
        });
    }

    const trailingBars = bars.slice(-resolvedBarCount).map((barHeight) => clampWaveformBarHeight(
        Number.isFinite(barHeight) ? barHeight : minBarHeight,
        minBarHeight,
        DEFAULT_AUDIO_WAVEFORM_MAX_BAR_HEIGHT,
    ));

    if (trailingBars.length === resolvedBarCount) {
        return trailingBars;
    }

    return [
        ...buildSilentAudioWaveformBars({
            barCount: resolvedBarCount - trailingBars.length,
            minBarHeight,
        }),
        ...trailingBars,
    ];
};

/**
 * Fits an existing waveform envelope into a new visible bar count. When we
 * have more source bars than room, preserve peaks by downsampling in buckets.
 * When we have fewer source bars than room, interpolate so short notes still
 * stretch naturally across the available width.
 */
export const fitAudioWaveformBarsToBarCount = ({
    bars,
    barCount = DEFAULT_AUDIO_WAVEFORM_BAR_COUNT,
    minBarHeight = DEFAULT_AUDIO_WAVEFORM_MIN_BAR_HEIGHT,
}: {
    bars: number[],
    barCount?: number,
    minBarHeight?: number,
}): number[] => {
    const resolvedBarCount = resolveWaveformBarCount(barCount);
    if (!Array.isArray(bars) || bars.length === 0) {
        return buildSilentAudioWaveformBars({
            barCount: resolvedBarCount,
            minBarHeight,
        });
    }

    const sanitizedBars = bars.map((barHeight) => clampWaveformBarHeight(
        Number.isFinite(barHeight) ? barHeight : minBarHeight,
        minBarHeight,
        DEFAULT_AUDIO_WAVEFORM_MAX_BAR_HEIGHT,
    ));
    if (sanitizedBars.length === resolvedBarCount) {
        return sanitizedBars;
    }

    if (sanitizedBars.length > resolvedBarCount) {
        return Array.from({ length: resolvedBarCount }, (_, index) => {
            const bucketStartIndex = Math.floor((index * sanitizedBars.length) / resolvedBarCount);
            const nextBucketStartIndex = Math.floor(
                ((index + 1) * sanitizedBars.length) / resolvedBarCount,
            );
            const bucketEndIndex = Math.max(
                bucketStartIndex + 1,
                nextBucketStartIndex,
            );
            let peakBarHeight = minBarHeight;

            for (
                let bucketIndex = bucketStartIndex;
                bucketIndex < Math.min(bucketEndIndex, sanitizedBars.length);
                bucketIndex += 1
            ) {
                if (sanitizedBars[bucketIndex] > peakBarHeight) {
                    peakBarHeight = sanitizedBars[bucketIndex];
                }
            }

            return peakBarHeight;
        });
    }

    if (sanitizedBars.length === 1) {
        return Array.from({ length: resolvedBarCount }, () => sanitizedBars[0]);
    }

    return Array.from({ length: resolvedBarCount }, (_, index) => {
        const sourcePosition = (index * (sanitizedBars.length - 1))
            / Math.max(1, resolvedBarCount - 1);
        const leftIndex = Math.floor(sourcePosition);
        const rightIndex = Math.min(sanitizedBars.length - 1, leftIndex + 1);
        const interpolationProgress = sourcePosition - leftIndex;

        return clampWaveformBarHeight(
            (sanitizedBars[leftIndex] * (1 - interpolationProgress))
            + (sanitizedBars[rightIndex] * interpolationProgress),
            minBarHeight,
            DEFAULT_AUDIO_WAVEFORM_MAX_BAR_HEIGHT,
        );
    });
};

const hashWaveformSeed = (seed: string): number => {
    let hash = 2166136261;

    for (let index = 0; index < seed.length; index += 1) {
        hash = normalizeUnsigned32(
            Math.imul(hash, 33) + seed.charCodeAt(index),
        );
    }

    return hash;
};

const stepWaveformSeed = (seed: number): number => (
    normalizeUnsigned32(Math.imul(seed, 1664525) + 1013904223)
);

/**
 * Produces a stable fallback waveform for cases where the browser cannot
 * decode the clip yet. A slight center bias keeps the bars looking intentional
 * instead of perfectly flat noise.
 */
export const buildDeterministicAudioWaveformBars = ({
    seed,
    barCount = DEFAULT_AUDIO_WAVEFORM_BAR_COUNT,
    minBarHeight = DEFAULT_AUDIO_WAVEFORM_MIN_BAR_HEIGHT,
}: {
    seed: string,
    barCount?: number,
    minBarHeight?: number,
}): number[] => {
    const resolvedBarCount = resolveWaveformBarCount(barCount);
    let nextSeed = hashWaveformSeed(seed || 'audio-waveform');

    return Array.from({ length: resolvedBarCount }, (_, index) => {
        nextSeed = stepWaveformSeed(nextSeed);
        const randomValue = nextSeed / 0xffffffff;
        const centerDistance = Math.abs(((index + 0.5) / resolvedBarCount) - 0.5) * 2;
        const centerBoost = 1 - centerDistance;
        const normalizedHeight = Math.min(
            1,
            (centerBoost * 0.55) + (randomValue * 0.35),
        );

        return normalizeWaveformBarHeight(
            normalizedHeight,
            minBarHeight,
            DEFAULT_AUDIO_WAVEFORM_MAX_BAR_HEIGHT,
        );
    });
};

/**
 * Smooths live analyser frames so the recording waveform reads as motion
 * instead of rapidly flickering independent bars.
 */
export const blendAudioWaveformBars = ({
    previousBars,
    nextBars,
    smoothing = DEFAULT_AUDIO_WAVEFORM_SMOOTHING,
}: {
    previousBars: number[],
    nextBars: number[],
    smoothing?: number,
}): number[] => {
    if (previousBars.length !== nextBars.length) {
        return nextBars;
    }

    const resolvedSmoothing = Number.isFinite(smoothing)
        ? Math.min(1, Math.max(0, smoothing))
        : DEFAULT_AUDIO_WAVEFORM_SMOOTHING;

    return nextBars.map((barHeight, index) => clampWaveformBarHeight(
        (previousBars[index] * resolvedSmoothing) + (barHeight * (1 - resolvedSmoothing)),
        DEFAULT_AUDIO_WAVEFORM_MIN_BAR_HEIGHT,
        DEFAULT_AUDIO_WAVEFORM_MAX_BAR_HEIGHT,
    ));
};

export const hasMeaningfulAudioWaveformBars = (bars: number[]): boolean => (
    Array.isArray(bars)
    && bars.some((barHeight) => barHeight > DEFAULT_AUDIO_WAVEFORM_MIN_BAR_HEIGHT + 0.08)
);

export {
    DEFAULT_AUDIO_WAVEFORM_BAR_COUNT,
    DEFAULT_AUDIO_WAVEFORM_BAR_GAP_PX,
    DEFAULT_AUDIO_WAVEFORM_MIN_BAR_HEIGHT,
    DEFAULT_AUDIO_WAVEFORM_BAR_WIDTH_PX,
};
